import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { NativeSelect } from "@/components/Field";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ReceiptViewer } from "@/components/Receipt";
import { ExpenseDialog } from "@/components/ExpenseDialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, money, thisYear, yearOptions } from "@/lib/format";
import { useSection } from "@/lib/sections";
import { useReportSec } from "@/components/ReportSecPicker";
import { BulkAssignBar, SelectBox } from "@/components/BulkAssignBar";
import { UnassignedToggle } from "@/components/UnassignedToggle";

function Totals({ cfg, rows }) {
  const tot = (sid) => rows.filter((r) => r.section_id === sid).reduce((a, r) => a + r.amount, 0);
  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-3">
      {cfg.expense_sections.map((s) => (
        <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`expense-total-${s.id}`}>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{s.name}</div>
          <div className="mt-1 font-mono text-2xl font-bold text-slate-900">{money(tot(s.id))}</div>
        </div>
      ))}
      <div className="rounded-xl bg-white p-5 ring-2 ring-[hsl(var(--gold))]" data-testid="expense-total-all">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Grand total expense</div>
        <div className="mt-1 font-mono text-2xl font-bold text-[hsl(var(--primary))]">{money(rows.reduce((a, r) => a + r.amount, 0))}</div>
      </div>
    </div>
  );
}

export default function Expenses() {
  const { hasArea, canEditRec, canBulk: bulkOn, isFullAdmin } = useApp();
  const canEdit = hasArea("finance");
  const canBulk = canEdit && bulkOn;
  const [sel, setSel] = useState([]);
  const [unassigned, setUnassigned] = useState(false);
  const [year, setYear] = useState(String(thisYear()));
  const [sec, setSec] = useState("");
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const section = useSection();
  const [rsec, setRsec] = useReportSec();
  const sk = section.key || "mwjk";
  useEffect(() => { setRsec(sk); }, [sk, setRsec]);
  useEffect(() => { api.get("/finance/config", { params: { sec: sk } }).then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); }, [sk, rsec]);
  const load = useCallback(() => api.get("/expenses", { params: { year: year || undefined, section_id: sec || undefined, ...(unassigned && isFullAdmin ? { unassigned: true } : { destination: section.name || undefined }) } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [year, sec, section.name, unassigned, isFullAdmin]);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return null;
  const itemName = (id) => cfg.expense_sections.flatMap((s) => s.items).find((i) => i.id === id)?.name || "(removed)";
  const secName = (id) => cfg.expense_sections.find((s) => s.id === id)?.name || id;
  const remove = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Expense deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader eyebrow={section.name ? `${section.name} finances` : "MWJK finances"} title="Expenses" subtitle={`Record ${section.name || "MWJK"} expenditures. They total automatically in the Annual Summary Report.`}>
        {canEdit && <Button onClick={() => setEdit({})} className="h-11 bg-amber-400 px-5 text-base font-bold text-slate-900 shadow-md hover:bg-amber-300" data-testid="expense-add-btn"><Plus className="mr-2 h-5 w-5" /> Add expense</Button>}
      </PageHeader>
      <div className="mb-6 flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="w-full sm:w-40"><NativeSelect value={year} onChange={setYear} placeholder="All years" options={yearOptions().map(String)} testid="expense-filter-year" /></div>
        <div className="w-full sm:w-64"><NativeSelect value={sec} onChange={setSec} placeholder="All sections" options={cfg.expense_sections.map((s) => ({ value: s.id, label: s.name }))} testid="expense-filter-section" /></div>
        {isFullAdmin && <UnassignedToggle on={unassigned} onChange={(v) => { setUnassigned(v); setSel([]); }} count={rows.length} testid="expense-unassigned-toggle" />}
      </div>
      <Totals cfg={cfg} rows={rows} />
      {canBulk && <BulkAssignBar kind="expenses" ids={sel.filter((id) => rows.some((r) => r.id === id))} onClear={() => setSel([])} onDone={load} />}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="expense-table">
          <thead><tr className="bg-[hsl(var(--primary))] text-left text-white">{canBulk && <th className="w-10 px-3 py-2.5"><SelectBox checked={rows.length > 0 && rows.every((r) => sel.includes(r.id))} onChange={(on) => setSel(on ? rows.map((r) => r.id) : [])} label="Select all" testid="expense-select-all" /></th>}{["Date", "Section", "Line item", "Paid to", "GC / MWJK", "Collection", "Description", "Amount", ""].map((h, i) => <th key={i} className={`whitespace-nowrap px-3 py-2.5 font-semibold ${i === 7 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={canBulk ? 10 : 9} className="px-3 py-8 text-center text-slate-500" data-testid="expense-empty">No expenses recorded for this filter.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 odd:bg-white even:bg-[hsl(var(--sand))]" data-testid={`expense-row-${r.id}`}>
                {canBulk && <td className="px-3 py-2"><SelectBox checked={sel.includes(r.id)} onChange={(on) => setSel((s) => (on ? [...s, r.id] : s.filter((x) => x !== r.id)))} label="Select expense" testid={`expense-select-${r.id}`} /></td>}
                <td className="whitespace-nowrap px-3 py-2">{fmtDate(r.date)}</td>
                <td className="px-3 py-2">{secName(r.section_id)}</td>
                <td className="px-3 py-2 font-semibold">{itemName(r.item_id)}</td>
                <td className="px-3 py-2">{r.paid_to}</td>
                <td className="whitespace-nowrap px-3 py-2" data-testid={`expense-destination-${r.id}`}>{r.destination || "—"}</td>
                <td className="px-3 py-2">{r.collection}</td>
                <td className="px-3 py-2 text-slate-600">{r.description}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono">{money(r.amount)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  {r.receipt_id && <Button variant="ghost" size="icon" onClick={() => setReceipt(r.receipt_id)} data-testid={`expense-receipt-${r.id}`}><Paperclip className="h-4 w-4" /></Button>}
                  {canEdit && canEditRec && <Button variant="ghost" size="icon" onClick={() => setEdit(r)} data-testid={`expense-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {canEdit && <ConfirmDelete onConfirm={() => remove(r.id)} title="Delete this expense?" testid={`expense-delete-${r.id}`} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ExpenseDialog open={!!edit} record={edit?.id ? edit : null} cfg={cfg} section={section.name} onClose={() => setEdit(null)} onSaved={load} />
      {receipt && <ReceiptViewer id={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
